import { useEffect, useMemo, useState } from 'react';
import {
  Layers, ChevronRight, TrendingUp, TrendingDown, Minus, Tag,
  Search, ArrowUpDown,
} from 'lucide-react';
import { toast } from 'sonner';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import VerloInsightPanel from '../../../components/reports/VerloInsightPanel';
import { reportsIntelApi } from '@verlo/shared/services/api';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Input } from '@verlo/shared/components/ui/input';
import { SearchField } from '@verlo/shared/components/ui/search-field';

const RANGES = [
  { value: 'today',    label: 'Today' },
  { value: 'wtd',      label: 'Week to date' },
  { value: 'mtd',      label: 'Month to date' },
  { value: 'last_30',  label: 'Last 30 days' },
  { value: 'last_90',  label: 'Last 90 days' },
  { value: 'ytd',      label: 'Year to date' },
];

const TIER_STYLES = {
  entry:   { bg: 'bg-paper',  text: 'text-ink-soft',   ring: 'ring-hairline',   label: 'Entry' },
  mid:     { bg: 'bg-signal-warn-tint',   text: 'text-signal-warn',   ring: 'ring-signal-warn-line',   label: 'Mid' },
  premium: { bg: 'bg-signal-info-tint',  text: 'text-signal-info',  ring: 'ring-signal-info-line',  label: 'Premium' },
};

const fmtMoney = (v) => v === null || v === undefined ? '—' : `£${Math.round(v).toLocaleString('en-GB')}`;
const fmtDelta = (v) => v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v}%`;

const ProductCategoryIntelligencePage = () => {
  const [range, setRange] = useState('last_30');
  const [cats, setCats] = useState(null);
  const [mix, setMix] = useState(null);
  const [products, setProducts] = useState(null);
  const [selectedCat, setSelectedCat] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: 'revenue', dir: 'desc' });

  const load = async () => {
    setLoading(true);
    try {
      const [c, m, p] = await Promise.all([
        reportsIntelApi.getCategories(range),
        reportsIntelApi.getSalesMix(range),
        reportsIntelApi.getProducts(range, selectedCat, undefined, 100),
      ]);
      setCats(c.data);
      setMix(m.data);
      setProducts(p.data);
    } catch {
      toast.error('Could not load product intelligence');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, selectedCat]);

  const filteredProducts = useMemo(() => {
    if (!products) return [];
    let rows = products.rows;
    if (search) rows = rows.filter((r) => r.name?.toLowerCase().includes(search.toLowerCase()) || String(r.plu || '') === search.trim());
    rows = [...rows].sort((a, b) => {
      const av = a[sort.key] ?? 0;
      const bv = b[sort.key] ?? 0;
      return sort.dir === 'desc' ? (bv - av) : (av - bv);
    });
    return rows;
  }, [products, search, sort]);

  const toggleSort = (key) => {
    setSort((s) => s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' });
  };

  return (
    <ReportsLayout>
      <div data-testid="product-intelligence-page">
        {/* Header */}
        <div className="flex min-h-[36px] items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <p className="text-[14px] text-ink-soft max-w-2xl">
              Category mix, per-product velocity, premium-tier movement and attachment rate, drillable from the estate down to a single PLU.
            </p>
          </div>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-[170px] h-9 text-[12px]" data-testid="prod-range-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Top row: 2 Verlo Insight cards + Sales Mix tile */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <VerloInsightPanel
            topic="products"
            range={range}
            label="Where the commercial opportunity lives"
            hint="A 2-sentence read on what's driving (or dragging) your category revenue right now."
          />
          <VerloInsightPanel
            topic="sales_mix"
            range={range}
            label="Premiumisation watch"
            hint="Is your customer trading up or down the menu? Verlo Insight scores the move in plain English."
          />
          {/* Sales mix visualiser */}
          <div className="bg-white border border-hairline/80 rounded-lg p-5" data-testid="sales-mix-card">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink-soft">Sales mix</div>
                <div className="text-sm font-semibold text-ink mt-0.5">Entry · Mid · Premium</div>
              </div>
              {mix && (
                <div className={`text-[12.5px] font-semibold ${mix.premiumisation_score > 0 ? 'text-signal-ok' : mix.premiumisation_score < 0 ? 'text-signal-stop' : 'text-ink-soft'}`}>
                  {mix.premiumisation_score > 0 ? '+' : ''}{mix.premiumisation_score}pp premium
                </div>
              )}
            </div>
            {mix && (
              <>
                <div className="flex h-3 rounded-full overflow-hidden border border-hairline">
                  {mix.rows.map((r) => {
                    const styles = TIER_STYLES[r.tier];
                    return (
                      <div key={r.tier} style={{ width: `${r.share_pct}%` }} className={`${styles.bg.replace('bg-', 'bg-')} ${r.tier === 'entry' ? 'bg-ink-faint' : r.tier === 'mid' ? 'bg-signal-warn-line' : 'bg-signal-info-line'}`} title={`${TIER_STYLES[r.tier].label}: ${r.share_pct}%`} />
                    );
                  })}
                </div>
                <div className="mt-3 space-y-2">
                  {mix.rows.map((r) => {
                    const Trend = r.share_delta > 0 ? TrendingUp : r.share_delta < 0 ? TrendingDown : Minus;
                    const trendColor = r.share_delta > 0 ? 'text-signal-ok' : r.share_delta < 0 ? 'text-signal-stop' : 'text-ink-faint';
                    return (
                      <div key={r.tier} className="flex items-center justify-between text-[12px]">
                        <span className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${r.tier === 'entry' ? 'bg-ink-faint' : r.tier === 'mid' ? 'bg-signal-warn-line' : 'bg-signal-info-line'}`} />
                          <span className="text-ink-soft font-medium">{TIER_STYLES[r.tier].label}</span>
                        </span>
                        <span className="flex items-center gap-2 font-mono-display tabular-nums">
                          <span className="text-ink">{r.share_pct}%</span>
                          <span className={`flex items-center gap-0.5 text-[12.5px] ${trendColor}`}>
                            <Trend size={10} />
                            {r.share_delta > 0 ? '+' : ''}{r.share_delta}pp
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Category strip — clickable to filter products */}
        <div className="mb-5" data-testid="category-strip">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13px] font-semibold text-ink">Category performance</h3>
            <span className="text-[12.5px] text-ink-soft">Click to drill into products</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <button
              onClick={() => setSelectedCat(null)}
              className={`text-left rounded-lg p-4 border transition-all ${
                selectedCat === null
                  ? 'bg-ink text-white border-ink '
                  : 'bg-white text-ink border-hairline hover:border-ink-faint'
              }`}
              data-testid="cat-all-btn"
            >
              <div className="text-[11px] uppercase tracking-[0.15em] font-semibold opacity-70">All categories</div>
              <div className="font-mono-display text-2xl font-medium mt-1.5 tabular-nums">{fmtMoney(cats?.total_revenue)}</div>
              <div className="text-[12.5px] opacity-60 mt-0.5">{cats?.category_count || 0} categories</div>
            </button>
            {cats?.rows.slice(0, 7).map((c) => {
              const active = selectedCat === c.category;
              const Trend = (c.delta_pct ?? 0) > 0 ? TrendingUp : (c.delta_pct ?? 0) < 0 ? TrendingDown : Minus;
              const trendColor = (c.delta_pct ?? 0) > 0 ? 'text-signal-ok' : (c.delta_pct ?? 0) < 0 ? 'text-signal-stop' : 'text-ink-faint';
              return (
                <button
                  key={c.category}
                  onClick={() => setSelectedCat(c.category)}
                  className={`text-left rounded-lg p-4 border transition-all ${
                    active
                      ? 'bg-ink text-white border-ink '
                      : 'bg-white text-ink border-hairline hover:border-ink-faint'
                  }`}
                  data-testid={`cat-btn-${c.category}`}
                >
                  <div className="text-[11px] uppercase tracking-[0.15em] font-semibold opacity-70 truncate">{c.category}</div>
                  <div className="flex items-baseline justify-between mt-1.5">
                    <div className="font-mono-display text-2xl font-medium tabular-nums">{fmtMoney(c.revenue)}</div>
                    {c.delta_pct !== null && (
                      <span className={`text-[12.5px] inline-flex items-center gap-0.5 font-mono-display ${active ? 'text-white/80' : trendColor}`}>
                        <Trend size={11} />{fmtDelta(c.delta_pct)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-1.5 text-[12.5px] opacity-70">
                    <span>GP {c.gp_pct}%</span>
                    <span className="flex items-center gap-1">
                      <span className={`w-1 h-1 rounded-full ${active ? 'bg-ink-faint' : 'bg-ink-faint'}`} />{c.tier_mix.entry}%
                      <span className={`w-1 h-1 rounded-full ${active ? 'bg-signal-warn-line' : 'bg-signal-warn-line'}`} />{c.tier_mix.mid}%
                      <span className={`w-1 h-1 rounded-full ${active ? 'bg-signal-info-line' : 'bg-signal-info-line'}`} />{c.tier_mix.premium}%
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Product table */}
        <div className="bg-white border border-hairline/80 rounded-lg overflow-hidden" data-testid="product-table">
          <div className="px-5 py-3 border-b border-hairline flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-semibold text-ink">
                {selectedCat ? selectedCat : 'All products'}
                <span className="text-[12.5px] text-ink-soft font-normal ml-2">({filteredProducts.length})</span>
              </h3>
              <p className="text-[12.5px] text-ink-soft mt-0.5">Ranked by revenue · click any column to re-sort</p>
            </div>
            <div className="flex items-center gap-2">
              <SearchField className="w-[200px]" placeholder="Search products" value={search} onChange={(e) => setSearch(e.target.value)} size="sm" data-testid="product-search" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-ink-soft">
                  <th className="text-left px-4 py-2.5 font-semibold w-[260px]">Product</th>
                  <th className="text-left px-2 py-2.5 font-semibold w-[80px]">Tier</th>
                  <th className="text-right px-3 py-2.5 font-semibold">
                    <button onClick={() => toggleSort('revenue')} className="inline-flex items-center gap-1 hover:text-ink">Revenue<ArrowUpDown size={10} /></button>
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold">
                    <button onClick={() => toggleSort('quantity')} className="inline-flex items-center gap-1 hover:text-ink">Qty<ArrowUpDown size={10} /></button>
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold">
                    <button onClick={() => toggleSort('asp')} className="inline-flex items-center gap-1 hover:text-ink">ASP<ArrowUpDown size={10} /></button>
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold">
                    <button onClick={() => toggleSort('gp_pct')} className="inline-flex items-center gap-1 hover:text-ink">GP%<ArrowUpDown size={10} /></button>
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold">
                    <button onClick={() => toggleSort('attachment_pct')} className="inline-flex items-center gap-1 hover:text-ink">Attach<ArrowUpDown size={10} /></button>
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold">
                    <button onClick={() => toggleSort('delta_pct')} className="inline-flex items-center gap-1 hover:text-ink">Δ vs prev<ArrowUpDown size={10} /></button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-ink-faint">Loading…</td></tr>
                )}
                {!loading && filteredProducts.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-ink-faint">No products match the current filters.</td></tr>
                )}
                {!loading && filteredProducts.map((p) => {
                  const tier = TIER_STYLES[p.tier] || TIER_STYLES.mid;
                  const Trend = (p.delta_pct ?? 0) > 0 ? TrendingUp : (p.delta_pct ?? 0) < 0 ? TrendingDown : Minus;
                  const trendColor = (p.delta_pct ?? 0) > 0 ? 'text-signal-ok' : (p.delta_pct ?? 0) < 0 ? 'text-signal-stop' : 'text-ink-faint';
                  return (
                    <tr key={p.id} className="border-t border-hairline hover:bg-hover-tint/50 group" data-testid={`product-row-${p.id}`}>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-ink">{p.name}</div>
                        <div className="text-[11px] uppercase tracking-wider text-ink-faint mt-0.5">{p.plu ? <span className="tabular-nums">{p.plu} · </span> : null}{p.category}</div>
                      </td>
                      <td className="px-2 py-2.5">
                        <span className={`inline-flex items-center gap-1 text-[12.5px] font-semibold px-1.5 py-0.5 rounded-md ${tier.bg} ${tier.text} ring-1 ${tier.ring}`}>
                          <Tag size={9} />{tier.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono-display font-semibold tabular-nums">{fmtMoney(p.revenue)}</td>
                      <td className="px-3 py-2.5 text-right font-mono-display tabular-nums text-ink-soft">{p.quantity.toLocaleString('en-GB')}</td>
                      <td className="px-3 py-2.5 text-right font-mono-display tabular-nums text-ink-soft">£{p.asp}</td>
                      <td className="px-3 py-2.5 text-right font-mono-display tabular-nums text-ink-soft">{p.gp_pct}%</td>
                      <td className="px-3 py-2.5 text-right font-mono-display tabular-nums text-ink-soft">{p.attachment_pct}%</td>
                      <td className="px-3 py-2.5 text-right">
                        {p.delta_pct !== null ? (
                          <span className={`inline-flex items-center gap-0.5 font-mono-display tabular-nums ${trendColor}`}>
                            <Trend size={11} />{fmtDelta(p.delta_pct)}
                          </span>
                        ) : (
                          <span className="text-ink-faint">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ReportsLayout>
  );
};

export default ProductCategoryIntelligencePage;
